import { type ReactElement } from 'react';
import { Search, ShieldCheck, BookOpen, TrafficCone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const SUPPORTED_LANGUAGES = [
  'Python',
  'JavaScript',
  'TypeScript',
  'Go',
  'PHP',
  'C/C++',
  'Ruby',
] as const;

const HOW_IT_WORKS = [
  {
    icon: Search,
    title: 'Proactively discover vulnerabilities',
    description:
      'Aggressively scans your code and understands what it does and where the risk is. Semantic analysis, not pattern matching.',
  },
  {
    icon: ShieldCheck,
    title: 'Confirm before reporting',
    description:
      'Every finding goes through multiple LLM stages that confirm whether the vulnerability is actually exploitable.',
  },
  {
    icon: BookOpen,
    title: 'Technical details',
    description:
      'New approaches to vulnerability discovery and verification. Read the paper, or run OpenAnt from GitHub.',
  },
] as const;

/**
 * KIR-3925: the Vulnerability Discovery with LLM page (AI AppSec). A
 * placeholder for the future OpenAnt integration: the preview content is
 * greyed out and inert behind an "under construction" note. Same static page
 * for the personal and organization views.
 */
export function LlmVulnDiscoveryPage(): ReactElement {
  return (
    <div className="relative">
      {/* Greyed, inert preview of the future OpenAnt screen. aria-hidden +
          pointer-events-none: it is decoration until the integration ships. */}
      <div
        aria-hidden
        className="pointer-events-none select-none opacity-40"
        data-testid="llm-vuln-discovery-preview"
      >
        <h2 data-page-title className="text-2xl font-bold">
          LLM Vulnerability Discovery
        </h2>
        <p className="mt-1 text-muted-foreground">
          Bring OpenAnt, Knostic&apos;s open source LLM based vulnerability
          scanner, into Kirin. This screen is under construction.
        </p>

        <Card className="mt-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <span className="rounded-md bg-muted px-3 py-1.5 text-sm font-semibold">
                OpenAnt
              </span>
              <Badge variant="outline">Open source</Badge>
            </div>
            <p className="mt-4 max-w-3xl text-sm text-muted-foreground">
              OpenAnt does not just flag issues. It uses LLMs to actually attack
              your code before it reports a finding, so you see real,
              exploitable vulnerabilities and almost no noise. It reads your
              code semantically, mapping each function with its callers,
              dependencies, and call context.
            </p>
            <div className="mt-4">
              <Button disabled>
                <Search className="mr-2 h-4 w-4" />
                Request a free scan
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-4">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">Up to 99.98%</div>
            <p className="mt-1 text-sm text-muted-foreground">
              of false positives eliminated on open source projects
            </p>
          </CardContent>
        </Card>

        <h3 className="mt-8 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          How it works
        </h3>
        <div className="mt-3 grid gap-4 md:grid-cols-3">
          {HOW_IT_WORKS.map(({ icon: Icon, title, description }) => (
            <Card key={title}>
              <CardHeader>
                <Icon className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-base">{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>

        <h3 className="mt-8 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Languages supported
        </h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {SUPPORTED_LANGUAGES.map(language => (
            <Badge key={language} variant="outline">
              {language}
            </Badge>
          ))}
        </div>

        <p className="mt-8 text-sm text-muted-foreground">
          Project page: <span className="font-medium">openant.knostic.ai</span>
          {'   '}GitHub:{' '}
          <span className="font-medium">github.com/knostic/OpenAnt</span>
          {'   '}Request a scan:{' '}
          <span className="font-medium">oss-scan@knostic.ai</span>
        </p>
      </div>

      {/* Under-construction note over the preview. */}
      <div className="absolute inset-0 flex items-start justify-center pt-24">
        <Card
          className="w-full max-w-xl shadow-xl"
          role="dialog"
          aria-label="OpenAnt integration is under construction"
        >
          <CardContent className="flex flex-col items-center gap-4 px-8 py-10 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-900/40">
              <TrafficCone className="h-7 w-7 text-amber-600 dark:text-amber-400" />
            </div>
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/40 dark:text-amber-300">
              UNDER CONSTRUCTION
            </Badge>
            <h2 className="text-2xl font-bold">
              OpenAnt integration is under construction
            </h2>
            <p className="text-sm text-muted-foreground">
              This screen will connect Kirin to OpenAnt, Knostic&apos;s open
              source LLM based vulnerability scanner. It finds real, exploitable
              vulnerabilities in the code your agents write, with almost no
              noise. The preview behind this note shows what is coming.
            </p>
            <a
              className="text-sm font-medium text-primary hover:underline"
              href="https://openant.knostic.ai"
              target="_blank"
              rel="noreferrer"
            >
              Learn more at openant.knostic.ai
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default LlmVulnDiscoveryPage;
